import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  DsMenuComponent,
  DsMenuItemDirective,
  DsMenuTriggerDirective,
} from 'src/app/shared/components/ds-menu/ds-menu.component';
import { DsTooltipDirective } from 'src/app/shared/components/ds-tooltip/ds-tooltip.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  INotificationItem,
  NotificationCategory,
  NotificationPriority,
  NotificationCategories,
} from 'src/app/modules/notification/notification.model';
import { trKey } from 'src/app/shared/utils/translate-safe';

/**
 * Ô icon tròn theo mức ưu tiên — tone trạng thái v2 (module-rail.jsx: info/success/warning/
 * error, nền soft + chữ đậm). Đọc token `status-*` nên tự đổi sáng/tối; bản cũ dùng palette
 * thô red/orange/blue kèm `dark:` thủ công và hex cam.
 */
const PRIORITY_TONE: Record<NotificationPriority, string> = {
  [NotificationPriority.Critical]: 'bg-status-error-soft text-status-error',
  [NotificationPriority.Urgent]: 'bg-status-warning-soft text-status-warning',
  [NotificationPriority.High]: 'bg-status-info-soft text-status-info',
  [NotificationPriority.Normal]: 'bg-status-info-soft text-status-info',
  [NotificationPriority.Low]: 'bg-warm-hover text-warm-secondary',
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'notification-panel',
  imports: [
    DsMenuComponent,
    DsMenuItemDirective,
    DsMenuTriggerDirective,
    DsTooltipDirective,
    TranslateModule,
  ],
  templateUrl: './notification-panel.component.html',
  styleUrls: ['./notification-panel.component.scss'],
})
export class NotificationPanelComponent {
  private _translate = inject(TranslateService);
  @Input() notifications: INotificationItem[] = [];
  @Input() isOpen = false;
  @Input() activeCategory: NotificationCategory | null = null;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() markAllRead = new EventEmitter<void>();
  @Output() notificationClicked = new EventEmitter<INotificationItem>();
  @Output() dismissed = new EventEmitter<string>();
  @Output() snoozed = new EventEmitter<{ id: string; minutes: number }>();
  @Output() categoryChanged = new EventEmitter<NotificationCategory | null>();
  @Output() viewAll = new EventEmitter<void>();

  categories = NotificationCategories;
  snoozeOptions = [
    { minutes: 30, label: 'Notification.SnoozeOption.30m' },
    { minutes: 60, label: 'Notification.SnoozeOption.1h' },
    { minutes: 1440, label: 'Notification.SnoozeOption.Tomorrow' },
  ];

  get filteredNotifications(): INotificationItem[] {
    if (this.activeCategory == null) return this.notifications;
    return this.notifications.filter((n) => n.category === this.activeCategory);
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  close(): void {
    this.isOpen = false;
    this.isOpenChange.emit(false);
  }

  onMarkAllRead(): void {
    this.markAllRead.emit();
  }

  onNotificationClick(notification: INotificationItem): void {
    this.notificationClicked.emit(notification);
  }

  onDismiss(event: Event, id: string): void {
    event.stopPropagation();
    this.dismissed.emit(id);
  }

  onSnooze(event: Event, id: string, minutes: number): void {
    event.stopPropagation();
    this.snoozed.emit({ id, minutes });
  }

  onCategoryChange(category: NotificationCategory | null): void {
    this.categoryChanged.emit(category);
  }

  getActiveCategoryName(): string {
    if (this.activeCategory == null) return this._translate.instant('Notification.Category.All');
    const cat = this.categories.find((c) => c.value === this.activeCategory);
    return cat ? trKey(this._translate, cat.translateKey) : '';
  }

  onViewAll(): void {
    this.viewAll.emit();
  }

  getTimeAgo(dateStr: string): string {
    const now = new Date();
    const diff = now.getTime() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0)
      return this._translate.instant('Notification.TimeAgo.Days', {
        count: days,
      });
    if (hours > 0)
      return this._translate.instant('Notification.TimeAgo.Hours', {
        count: hours,
      });
    if (minutes > 0)
      return this._translate.instant('Notification.TimeAgo.Minutes', {
        count: minutes,
      });
    return this._translate.instant('Notification.TimeAgo.JustNow');
  }

  /** Lớp tone cho ô icon của một thông báo. */
  toneClass(priority: NotificationPriority): string {
    return PRIORITY_TONE[priority] ?? PRIORITY_TONE[NotificationPriority.Normal];
  }

  getCategoryIcon(category: NotificationCategory): string {
    return this.categories.find((c) => c.value === category)?.icon || 'notifications';
  }
}
